import { getApiOrigin } from "@utils/apiOrigin";

export interface BillingAddonsSettingsResponse {
  success?: boolean;
  message?: string;
  response?: {
    enabled?: boolean;
  };
}

export interface BillingAddonsSettings {
  enabled: boolean;
}

const BILLING_ADDONS_SETTINGS_PATH = "/controls/billing-addons/settings";

let cachedSettings: BillingAddonsSettings | null = null;
let pendingSettingsPromise: Promise<BillingAddonsSettings> | null = null;

/**
 * Whether the rules server shows the Billing Add-ons tab on Payments and Billing.
 * Default true when the endpoint is unreachable.
 */
export async function getBillingAddonsSettings(
  forceRefresh = false,
): Promise<BillingAddonsSettings> {
  if (!forceRefresh && cachedSettings) {
    return cachedSettings;
  }

  if (!forceRefresh && pendingSettingsPromise) {
    return pendingSettingsPromise;
  }

  pendingSettingsPromise = (async () => {
    const baseUrl = getApiOrigin();
    const candidateUrls: string[] = [];
    const addCandidate = (url: string) => {
      if (!candidateUrls.includes(url)) {
        candidateUrls.push(url);
      }
    };

    if (baseUrl) {
      const normalizedBase = baseUrl.replace(/\/+$/, "");
      addCandidate(`${normalizedBase}${BILLING_ADDONS_SETTINGS_PATH}`);
      addCandidate(`${normalizedBase}/api/v1${BILLING_ADDONS_SETTINGS_PATH}`);
    }

    addCandidate(BILLING_ADDONS_SETTINGS_PATH);
    addCandidate(`/api/v1${BILLING_ADDONS_SETTINGS_PATH}`);

    for (const url of candidateUrls) {
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          continue;
        }
        const data = (await response.json()) as BillingAddonsSettingsResponse;
        const settings: BillingAddonsSettings = {
          enabled: data?.response?.enabled !== false,
        };
        cachedSettings = settings;
        return settings;
      } catch {
        // Try next candidate URL.
      }
    }

    return { enabled: true };
  })();

  try {
    return await pendingSettingsPromise;
  } finally {
    pendingSettingsPromise = null;
  }
}
